import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {z} from 'zod';
import {Observable} from 'rxjs';
import {DateTime} from 'luxon';
import {environment} from 'src/environments/environment';
import {
    downloadResponseAsFile,
    mapParseItem,
    TranslateService,
    zDateTime,
    zDateTimeAsSent,
    zNumberToString,
} from '@brunata-angular/util';
import {
    outputFormatFileMeta,
    SavedExportOutputFormat,
} from '../../export/shared/models/output-format.model';

const locationsExportFormat: SavedExportOutputFormat = 'EXCEL';

@Injectable({
    providedIn: 'root',
})
export class LocationService {
    private http = inject(HttpClient);
    private readonly translateService = inject(TranslateService);

    getLocation(buildingNo: string, locationId: string): Observable<Location> {
        return this.http
            .get(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations/${locationId}/consumers`,
            )
            .pipe(mapParseItem(locationSchema));
    }

    getLocations(buildingNo: string): Observable<Locations> {
        return this.http
            .get(this.locationsUrl(buildingNo), {
                params: {includeallconsumers: 'true'},
            })
            .pipe(mapParseItem(locationsSchema));
    }

    downloadLocationsExport(
        buildingNo: string,
        consumerIds: string[],
    ): Observable<HttpResponse<Blob>> {
        const {mimeType, extension} = outputFormatFileMeta[locationsExportFormat];
        return this.http
            .post(
                `${environment.onlineServiceUrl}/buildings/${buildingNo}/exports/consumers`,
                {
                    format: locationsExportFormat,
                    languagetag: this.translateService.language,
                    consumerIds,
                },
                {
                    observe: 'response',
                    responseType: 'blob',
                },
            )
            .pipe(
                downloadResponseAsFile(
                    () =>
                        `Brunata-Consumers-${buildingNo}-${DateTime.now().toFormat('yyyy-MM-dd')}.${extension}`,
                    mimeType,
                ),
            );
    }

    private locationsUrl(buildingNo: string): string {
        return `${environment.onlineServiceUrl}/buildings/${buildingNo}/locations`;
    }
}

export const consumerSchema = z.object({
    name: z.string(),
    consumerId: zNumberToString,
});

const locationsConsumerSchema = z.object({
    consumerId: zNumberToString,
    consumerNo: z.string().nullable(),
    name: z.string().nullable(),
    occupationDate: zDateTimeAsSent.nullable(),
    occupationEndDate: zDateTimeAsSent.nullable(),
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    isPhoneMobile: z.boolean().nullable(),
    username: z.string().nullable(),
    onlineAccess: z.string(),
    invitedDate: zDateTime.nullable(),
    invitedByPdf: zDateTime.nullable(),
    invitedByEmail: zDateTime.nullable(),
    accountCreatedDate: zDateTime.nullable(),
    lastLoginDate: zDateTime.nullable(),
});

const locationsLocationSchema = z.object({
    locationId: zNumberToString,
    propertyNo: z.string().nullable(),
    branchNo: z.string().nullable(),
    locationNo: z.string().nullable(),
    street: z.string().nullable(),
    stairway: z.string().nullable(),
    floor: z.string().nullable(),
    postalCode: z.string().nullable(),
    countryCode: z.string().nullable(),
    city: z.string().nullable(),
    dismantledDate: zDateTime.nullable(),
    virtual: z.boolean(),
});

const locationSchema = z.object({
    location: locationsLocationSchema,
    consumers: z.array(locationsConsumerSchema),
});

const locationsFixedLocationAlloUnitSchema = z.object({
    locationAlloUnitId: zNumberToString,
    alloUnitType: z.string(),
});

const locationWithDetailsSchema = z.object({
    location: locationsLocationSchema,
    consumers: z.array(locationsConsumerSchema),
    meters: z.array(z.unknown()),
    fixedLocationAlloUnits: z.array(locationsFixedLocationAlloUnitSchema),
});

const locationsBuildingSchema = z.object({
    buildingBasicDataId: zNumberToString,
    buildingNo: zNumberToString,
    buildingName: z.string().nullable(),
    address1: z.string().nullable(),
    address2: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    buildingType: z.string(),
    phoneCode: z.string().nullable(),
    dataPrivacyRetentionLength: z.number().nullable(),
});

export const locationsSchema = z.object({
    building: locationsBuildingSchema,
    locations: z.array(locationWithDetailsSchema),
});

export type Consumer = z.infer<typeof consumerSchema>;
export type Location = z.infer<typeof locationSchema>;
export type Locations = z.infer<typeof locationsSchema>;
export type LocationsBuilding = z.infer<typeof locationsBuildingSchema>;
export type LocationsLocation = z.infer<typeof locationsLocationSchema>;
export type LocationsConsumer = z.infer<typeof locationsConsumerSchema>;
export type LocationsFixedLocationAlloUnit = z.infer<typeof locationsFixedLocationAlloUnitSchema>;
export type LocationWithDetails = z.infer<typeof locationWithDetailsSchema>;
